"use client";
import React, { useState, useEffect } from "react";
import { captureLead } from "@helpers/leadCapture";
import { IVariantDetailsPrice } from "../CustomCard/CustomCard.types";
import CustomCardV2 from "../CustomCard/CustomCardV2";
import styles from "./CardListPrice.module.scss";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { setLeadData } from "@utils/leadDataStorage";

interface CardListV2Props {
  variants: IVariantDetailsPrice[];
  isLoggedIn: boolean;
  city: any;
  brandName: string;
  modelName: string;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
}

export default function CardListV2({
  variants,
  isLoggedIn,
  city,
  brandName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  savingBalance
}: CardListV2Props) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [jsEnabled, setJsEnabled] = useState(false);
  
  const variantsToShow = variants;

  useEffect(() => {
    setJsEnabled(true);
  }, []);

  const handleToggle = async (index: number) => {
    const prevIdx = expandedIndex;
    const variantName = variantsToShow?.[index]?.variantName;
    setExpandedIndex((prev) => (prev === index ? null : index));
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_variant",
        car_availability: carAvailability,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        city_name: city?.name,
        Section: "Price page summary",
      },
    });
    if (prevIdx !== index && isLoggedIn) {
      await captureLead({
        pageType,
        carAvailability,
        brandName,
        modelName,
        variantName,
        cityName: city?.name,
        vehicleColor: "",
        loginTypeLabel: "ViewedDetailedPrice",
        componentName: "CardListV2",
        onLeadDataMerged: setLeadData,
        flowTypeLabel: "New Car",
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={`${styles.cardWrapper} ${jsEnabled && !showMore ? styles.hideExtraVariants : ""}`}>  
        {variantsToShow.map((variant, index) => (
          <div
            key={variant.slug}
            className={jsEnabled && index >= 4 && !showMore ? styles.hiddenVariant : ""}
          >
            <CustomCardV2
              data={variant}
              isExpanded={expandedIndex === index}
              onToggle={() => handleToggle(index)}
              isLoggedIn={isLoggedIn}
              city={city}
              brandName={brandName}
              modelName={modelName}
              isSellable={variant.status === "sellable"}
              pageType={pageType}
              carAvailability={carAvailability}
              savingBalance={savingBalance}
            />
          </div>
        ))}
      </div>
      {jsEnabled && variants.length > 4 && (
        <div
          className={styles.viewMoreLink}
          onClick={() => setShowMore(!showMore)}
        >
          {showMore
            ? `View less variants`
            : `View ${variants.length - 4} more variants`}
          <Image
            src={`${assetLoader()}/icons/Chevron_down.svg`}
            alt="arrow-right"
            width={24}
            height={24}
            className={`${showMore ? styles.iconRotate : ""}`}
          />
        </div>
      )}
    </div>
  );
}
